import React from 'react';
import { 
  ArrowLeft, 
  ExternalLink, 
  BookOpen, 
  Lightbulb, 
  Users, 
  Settings,
  HelpCircle,
  X
} from 'lucide-react';
import { useHelp } from '../../context/HelpContext';
import '../../styles/asset-lifecycle-modal.css';

const PageHelp = () => {
  const { isPageHelpOpen, currentPageHelp, closePageHelp } = useHelp();
  
  if (!isPageHelpOpen) return null;

  // Mock page help content - in a real app, this would be dynamic based on pageName
  const getPageHelpContent = (page) => {
    const helpContent = {
      'asset-registry': {
        title: 'Asset Registry Help',
        description: 'Learn how to manage your asset inventory effectively.',
        sections: [
          {
            title: 'Adding New Assets',
            content: 'Click the "New Asset" button to add assets. Fill in required fields like Asset Name, Category, and Location.'
          },
          {
            title: 'Asset Categories',
            content: 'Organize assets using categories. You can create custom categories or use predefined ones.'
          },
          {
            title: 'Asset Search & Filtering',
            content: 'Use the search bar and filters to quickly find specific assets. You can filter by category, status, location, and more.'
          }
        ],
        relatedLinks: [
          { title: 'Asset Lifecycle Management', href: '#asset-lifecycle' },
          { title: 'Asset Categorization', href: '#asset-categorization' },
          { title: 'Financial Valuation', href: '#financial-valuation' }
        ]
      },
      'contract-management': {
        title: 'Contract Management Help',
        description: 'Master contract creation, editing, and lifecycle management.',
        sections: [
          {
            title: 'Creating Contracts',
            content: 'Start with a template or create from scratch. Use the contract wizard for guided creation.'
          },
          {
            title: 'Contract Templates',
            content: 'Templates speed up contract creation. Customize templates to match your organization\'s needs.'
          },
          {
            title: 'Contract Approval Workflow',
            content: 'Set up approval workflows to ensure proper review and authorization of contracts.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Templates', href: '#contract-templates' },
          { title: 'E-Signature', href: '#e-signature' },
          { title: 'Renewal Alerts', href: '#renewal-alerts' }
        ]
      },
      'ai-insights': {
        title: 'AI Insights Help',
        description: 'Leverage AI-powered analytics and recommendations for better decision making.',
        sections: [
          {
            title: 'Understanding AI Insights',
            content: 'AI Insights provide automated analysis of your data to identify trends, risks, and opportunities.'
          },
          {
            title: 'Risk Scoring',
            content: 'AI analyzes contracts and assets to provide risk scores based on historical data and industry standards.'
          },
          {
            title: 'Predictive Analytics',
            content: 'Get forecasts and predictions based on your data patterns to make informed decisions.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Drafting', href: '#ai-contract-drafting' },
          { title: 'Search & Q&A', href: '#ai-search-qa' },
          { title: 'Predictive Analytics', href: '#predictive-analytics' }
        ]
      },
      'fixed-price-contracts': {
        title: 'Fixed-Price Contract Management Help',
        description: 'Learn how to effectively manage fixed-price contracts with scope, deliverables, and milestone tracking.',
        sections: [
          {
            title: 'Overview',
            content: 'The Fixed-Price Contract Management module allows you to manage contracts with predetermined pricing. Track key metrics including total contracts, active contracts, total contract value, and change requests through the Overview tab.'
          },
          {
            title: 'Creating Fixed-Price Contracts',
            content: 'Click the "New Fixed-Price Contract" button in the header to create a new contract. You\'ll need to define the contract scope, deliverables, milestones, and total contract value. All pricing is fixed at the time of contract creation.'
          },
          {
            title: 'KPI Cards',
            content: 'The Overview tab displays four key performance indicators: Total Contracts (blue), Active Contracts (green), Total Value (yellow), and Change Requests (red). These cards provide a quick snapshot of your fixed-price contract portfolio.'
          },
          {
            title: 'Contracts Tab',
            content: 'View all your fixed-price contracts in a comprehensive table. You can see contract details, client information, status, value, progress, milestones completion, quality scores, and risk levels. Use the action buttons to view, edit, or delete contracts.'
          },
          {
            title: 'Milestones Tab',
            content: 'Track contract milestones with their associated dates, values, and completion status. Milestones can be marked as completed, in-progress, or pending. This helps you monitor project progress and payment schedules.'
          },
          {
            title: 'Scope & Deliverables Tab',
            content: 'Manage the detailed scope of work and deliverables for each contract. Scope items define what work will be performed, while deliverables specify what will be produced. This ensures clear expectations and accountability.'
          },
          {
            title: 'Change Requests',
            content: 'Fixed-price contracts may require changes during execution. Track change requests to monitor scope modifications, additional work, or adjustments to deliverables. Each change request should be reviewed and approved before implementation.'
          },
          {
            title: 'Best Practices',
            content: '1. Clearly define scope and deliverables upfront. 2. Set realistic milestones with appropriate values. 3. Monitor progress regularly through the Milestones tab. 4. Document all change requests. 5. Review quality scores and risk levels to identify potential issues early.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Management', href: '#contract-management' },
          { title: 'Contract Templates', href: '#contract-templates' },
          { title: 'Milestone Tracking', href: '#milestone-tracking' }
        ]
      },
      'contract-lifecycle': {
        title: 'Contract Lifecycle Management Help',
        description: 'Learn how to manage contract versioning, renewals, terminations, and lifecycle events effectively.',
        sections: [
          {
            title: 'Overview',
            content: 'The Contract Lifecycle Management module provides comprehensive tracking of contracts throughout their entire lifecycle. Monitor key metrics including total contracts, active contracts, pending renewals, total value, critical risks, and compliance status.'
          },
          {
            title: 'Contract Lifecycle Tab',
            content: 'View and manage all contracts in the lifecycle tab. Use the search functionality to find specific contracts by title, ID, or counterparty. Click on actions to view, edit, or delete contracts.'
          },
          {
            title: 'Renewals Tab',
            content: 'Access the Renewals Management page to manage contract renewals, automated alerts, and renewal processes with comprehensive tracking and notifications.'
          },
          {
            title: 'Terminations Tab',
            content: 'The Terminations tab will allow you to manage contract terminations and track termination events. This feature is coming soon.'
          },
          {
            title: 'Analytics Tab',
            content: 'Access comprehensive analytics, performance metrics, and detailed reporting for your contract portfolio through the Analytics Dashboard.'
          },
          {
            title: 'KPI Cards',
            content: 'Monitor key performance indicators at a glance: Total Contracts, Active Contracts, Pending Renewals, Total Value, Critical Risks, and Non-Compliant contracts.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Renewals & Alerts', href: '/contracts/renewals-alerts' },
          { title: 'Contract Analytics', href: '/contracts/analytics' },
          { title: 'Contract Templates', href: '/contracts/templates' }
        ]
      },
      'contract-analytics': {
        title: 'Contract Analytics & Reporting Help',
        description: 'Learn how to use comprehensive analytics and reporting features for contract management.',
        sections: [
          {
            title: 'Overview',
            content: 'The Contract Analytics & Reporting module provides comprehensive insights into your contract portfolio. Monitor key metrics including total contracts, active contracts, expiring contracts, and total contract value through interactive KPI cards and charts.'
          },
          {
            title: 'KPI Cards',
            content: 'The Overview tab displays four key performance indicators: Total Contracts (blue), Active Contracts (green), Expiring Soon (yellow), and Total Value (purple). These cards provide a quick snapshot of your contract portfolio performance.'
          },
          {
            title: 'Contract Trends Chart',
            content: 'View contract trends over the last 6 months with a bar chart showing both the number of contracts and their total value by month. This helps identify patterns and seasonal trends in contract creation.'
          },
          {
            title: 'Contracts by Type',
            content: 'The pie chart displays the distribution of contracts by type, showing which contract types are most common in your portfolio. This helps with resource allocation and planning.'
          },
          {
            title: 'Contracts by Status',
            content: 'The line chart shows the distribution of contracts by status (Active, Draft, Pending Approval, etc.), helping you understand the current state of your contract portfolio.'
          },
          {
            title: 'Reports Tab',
            content: 'Access and manage generated reports in the Reports tab. You can view, download, and delete reports. Use the "Generate Report" button to create new reports.'
          },
          {
            title: 'Refreshing Data',
            content: 'Click the refresh button in the header to reload analytics data from the backend. This ensures you always have the most up-to-date information.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Lifecycle', href: '/contracts/lifecycle' },
          { title: 'Contract Management', href: '#contract-management' },
          { title: 'Dashboard', href: '/dashboard' }
        ]
      },
      'time-materials-contracts': {
        title: 'Time & Materials Contract Management Help',
        description: 'Learn how to effectively manage time & materials contracts with hourly rates, time tracking, and cost management.',
        sections: [
          {
            title: 'Overview',
            content: 'The Time & Materials Contract Management module allows you to manage contracts based on actual time worked and materials used. Track key metrics including total contracts, hours logged, total cost, and average utilization through the Overview tab.'
          },
          {
            title: 'Creating T&M Contracts',
            content: 'Click the "New T&M Contract" button in the header to create a new contract. You\'ll need to define hourly rates by role, material cost categories with markups, time tracking requirements, and billing rules. Pricing is based on actual time and materials used.'
          },
          {
            title: 'KPI Cards',
            content: 'The Overview tab displays four key performance indicators: Total Contracts (blue), Hours Logged (green), Total Cost (yellow), and Average Utilization (red). These cards provide a quick snapshot of your time & materials contract portfolio performance.'
          },
          {
            title: 'Contracts Tab',
            content: 'View all your time & materials contracts in a comprehensive table. You can see contract details, client information, status, hourly rates, hours logged, total cost, utilization rates, efficiency scores, and risk levels. Use the action buttons to view, edit, or delete contracts.'
          },
          {
            title: 'Time Tracking Tab',
            content: 'Monitor time tracking configuration and cost tracking for each contract. View time tracking settings including enabled status, required tracking, and approval requirements. Track costs against budget ceilings and monitor cost efficiency percentages.'
          },
          {
            title: 'Rates & Costs Tab',
            content: 'Manage hourly rates by role and material cost categories for each contract. Set different rates for different roles (e.g., Senior Developer, Junior Developer, Project Manager). Configure material cost categories with appropriate markup percentages.'
          },
          {
            title: 'Hourly Rates',
            content: 'Define role-based hourly rates for different positions. Each role can have its own rate, allowing for accurate billing based on the skill level and responsibilities of the person performing the work.'
          },
          {
            title: 'Material Costs',
            content: 'Track material costs with category-based markups. Common categories include software licenses, hardware, third-party services, and travel expenses. Markups can be applied to cover overhead and profit margins.'
          },
          {
            title: 'Billing Rules',
            content: 'Configure billing rules including minimum billable hours, rounding rules (quarter-hour, half-hour), travel time billing, and administrative time tracking. These rules ensure consistent and fair billing practices.'
          },
          {
            title: 'Best Practices',
            content: '1. Set realistic hourly rates based on market rates and skill levels. 2. Configure appropriate material cost markups. 3. Enable time tracking and require approvals for accurate billing. 4. Monitor utilization rates to optimize resource allocation. 5. Set cost ceilings to prevent budget overruns. 6. Review efficiency scores regularly to identify improvement opportunities.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Management', href: '#contract-management' },
          { title: 'Time Tracking', href: '#time-tracking' },
          { title: 'Cost Management', href: '#cost-management' }
        ]
      },
      'e-signature': {
        title: 'E-Signature Management Help',
        description: 'Learn how to manage electronic signatures, track document status, and monitor signing progress.',
        sections: [
          {
            title: 'Overview',
            content: 'The E-Signature Management module allows you to manage electronic signatures for contracts and documents. Track key metrics including total documents, pending signatures, in-progress documents, completed signatures, urgent documents, and completion rate through the KPI cards.'
          },
          {
            title: 'Tabs Overview',
            content: 'The module has five tabs: Pending Signatures (documents awaiting signatures), In Progress (documents being signed), Completed (fully signed documents), Expired (documents that expired without signatures), and Analytics (signature trends and insights).'
          },
          {
            title: 'Pending Signatures Tab',
            content: 'View all documents that are waiting for signatures. You can see document details, recipients, progress, and due dates. Use the action buttons to view details, send reminders, or manage documents.'
          },
          {
            title: 'In Progress Tab',
            content: 'Monitor documents that are currently being signed. Track completion percentage and see which recipients have signed and which are still pending.'
          },
          {
            title: 'Completed Tab',
            content: 'View all successfully completed signature documents. These are contracts and documents that have been fully signed by all required parties.'
          },
          {
            title: 'Expired Tab',
            content: 'Review documents that expired without receiving all required signatures. These documents may need to be re-sent or cancelled.'
          },
          {
            title: 'Analytics Tab',
            content: 'Access comprehensive analytics including documents by status (pie chart), documents by priority (bar chart), and completion trends over time (line chart). AI-powered insights provide recommendations for improving signature workflows.'
          },
          {
            title: 'KPI Cards',
            content: 'The Overview displays six key performance indicators: Total Documents (blue), Pending Signatures (orange), In Progress (blue), Completed (green), Urgent Documents (red), and Completion Rate (purple). These cards provide a quick snapshot of your e-signature portfolio.'
          },
          {
            title: 'Sending Reminders',
            content: 'Click the reminder icon on any pending document to send a reminder email to recipients who haven\'t signed yet. Reminders help ensure timely completion of signature workflows.'
          },
          {
            title: 'Document Status',
            content: 'Document status is automatically determined based on contract status: Pending Signature (new documents), In Progress (documents being reviewed/signed), Completed (fully executed contracts), and Expired (expired or cancelled contracts).'
          },
          {
            title: 'Priority Levels',
            content: 'Documents are automatically marked as High priority if they are expiring within 7 days. You can also manually set priority levels when creating documents.'
          },
          {
            title: 'Best Practices',
            content: '1. Monitor the Urgent Documents KPI to identify documents needing immediate attention. 2. Use the Analytics tab to track completion trends and identify bottlenecks. 3. Send reminders proactively for documents approaching their due dates. 4. Review expired documents regularly to determine if they should be re-sent. 5. Use the completion rate metric to measure the efficiency of your signature workflows.'
          }
        ],
        relatedLinks: [
          { title: 'Contract Management', href: '#contract-management' },
          { title: 'Contract Templates', href: '#contract-templates' },
          { title: 'Contract Lifecycle', href: '#contract-lifecycle' }
        ]
      }
    };

    return helpContent[page] || {
      title: `${page || 'Page'} Help`,
      description: 'Detailed help content for this page is coming soon.',
      sections: [
        {
          title: 'Getting Started',
          content: 'This page help is under development. Please refer to the Global Help Center for general guidance.'
        }
      ],
      relatedLinks: [
        { title: 'Global Help Center', href: '#global-help' }
      ]
    };
  };

  const content = getPageHelpContent(currentPageHelp);

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        animation: 'fadeIn 0.2s ease-in-out'
      }}
      onClick={closePageHelp}
    >
      <div 
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: '800px',
          height: '100vh',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInFromRight 0.3s ease-out',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          backgroundColor: '#4678be',
          color: 'white',
          flexShrink: 0,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1
          }}>
            <button 
              onClick={closePageHelp}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '0',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
              title="Back to page"
            >
              <ArrowLeft size={20} />
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <BookOpen size={24} />
              <div>
                <h1 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'white',
                  fontFamily: 'var(--font-family)'
                }}>
                  {content.title}
                </h1>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: 'var(--font-family)'
                }}>
                  {content.description}
                </p>
              </div>
            </div>
          </div>
          <button 
            onClick={closePageHelp}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0',
              color: 'white',
              cursor: 'pointer',
              fontSize: '24px',
              lineHeight: '1',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title="Close help"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          display: 'flex',
          gap: '24px'
        }}>
          <div style={{
            flex: 1,
            minWidth: 0
          }}>
            {content.sections.map((section, index) => (
              <div 
                key={index} 
                style={{
                  marginBottom: '32px',
                  paddingBottom: '24px',
                  borderBottom: index < content.sections.length - 1 ? '1px solid var(--border-color)' : 'none'
                }}
              >
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: '0 0 12px 0',
                  fontFamily: 'var(--font-family)'
                }}>
                  {section.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  {section.content}
                </p>
              </div>
            ))}

            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border-color)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: '0 0 16px 0',
                fontFamily: 'var(--font-family)'
              }}>
                Related Help Topics
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {content.relatedLinks.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.href} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#4678be',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      padding: '8px 0',
                      borderBottom: '1px solid var(--border-color)',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#3567a3';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#4678be';
                    }}
                  >
                    <ExternalLink size={16} />
                    {link.title}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div style={{
            width: '300px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '20px',
              border: '1px solid var(--border-color)',
              borderRadius: '0'
            }}>
              <h4 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: '0 0 16px 0',
                fontFamily: 'var(--font-family)'
              }}>
                <Lightbulb size={18} /> Quick Tips
              </h4>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                listStyle: 'disc',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                lineHeight: '1.8'
              }}>
                <li>Use keyboard shortcuts for faster navigation</li>
                <li>Save frequently used filters as views</li>
                <li>Export data for external analysis</li>
                <li>Set up alerts for important changes</li>
              </ul>
            </div>

            <div style={{
              backgroundColor: '#f8fafc',
              padding: '20px',
              border: '1px solid var(--border-color)',
              borderRadius: '0'
            }}>
              <h4 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: '0 0 12px 0',
                fontFamily: 'var(--font-family)'
              }}>
                <HelpCircle size={18} /> Need More Help?
              </h4>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                margin: '0 0 16px 0',
                fontFamily: 'var(--font-family)'
              }}>
                Can't find what you're looking for?
              </p>
              <a 
                href="mailto:support@assetcontractpro.com" 
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#4678be',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '0',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#3567a3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#4678be';
                }}
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageHelp;
